import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { apiGet } from '../utils/api';
import { useLatestRequest } from '../hooks/useSearchRequest';
import { titleCaseCategory, CATEGORY_OPTIONS } from '../constants/riskAssessment';
import { BTN_XS } from '../constants/buttons';

/**
 * Inline chevron — mirrors the one in UserAutocomplete (intentional copy,
 * keeps this component self-contained so it can be deleted independently).
 */
const ChevronDown = ({ className }) => (
  <svg className={className} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
    <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z" clipRule="evenodd" />
  </svg>
);

/**
 * Hazard combobox for the Risk Assessment detail page.
 *
 * Mirrors the structure of {@link ./UserAutocomplete.js} (debounce + outside-
 * click + ARIA combobox + rotating chevron) but swapped onto the
 * `/api/risk-assessments/blocks?search=` endpoint which returns objects like:
 *   { kind, id, name, category, seed_slug, description, row_count, ... }
 *
 * Props:
 *  - value: string — current hazard text
 *  - onChange(value): called on free-text typing (no library selection)
 *  - onSelect(block): called when user picks a suggestion from the dropdown
 *  - onSaveAsBlock(value, { toWorkspace, toDept }): called on blur if at
 *      least one save-target checkbox is ticked. Only rendered when at
 *      least one of canSaveToWorkspace / canSaveToDept is true.
 *  - canSaveToWorkspace: bool — show "save to my workspace" checkbox
 *  - canSaveToDept: bool — show "save to department library" checkbox
 *  - disabled: bool
 *  - placeholder: string
 */
const DEFAULT_INPUT_CLASS = 'block w-full pl-3 pr-9 py-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-70';

let hazardAutocompleteSeq = 0;
function useHazardListboxId() {
  const ref = useRef(null);
  if (ref.current === null) {
    hazardAutocompleteSeq += 1;
    ref.current = `hazard-autocomplete-listbox-${hazardAutocompleteSeq}`;
  }
  return ref.current;
}

export default function HazardAutocomplete({
  value,
  onChange,
  onSelect,
  onSaveAsBlock,
  onBlur,
  canSaveToWorkspace = false,
  canSaveToDept = false,
  disabled = false,
  placeholder = 'Type a hazard or pick from library…',
}) {
  const listboxId = useHazardListboxId();
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [hasSelected, setHasSelected] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasFetchedOnce, setHasFetchedOnce] = useState(false);
  // "The library said no" vs "we never got an answer". Without this term the
  // empty-state below claims the second is the first — see fetchSuggestions.
  const [fetchFailed, setFetchFailed] = useState(false);
  // Save-target checkboxes. Default: dept ticked for curators (preserves
  // current behaviour for buyers); workspace off-by-default to keep saves
  // intentional. Regular users see only workspace, off-by-default.
  const [saveToWorkspace, setSaveToWorkspace] = useState(false);
  const [saveToDept, setSaveToDept] = useState(false);
  // Optional category for the saved block. Empty string = uncategorised.
  const [saveCategory, setSaveCategory] = useState('');
  // Tracks which targets have ALREADY been saved for the current query, so
  // re-ticking / re-blurring doesn't fire a second POST (server returns 409
  // on duplicate name). Reset whenever the query text changes so the next
  // distinct hazard is a fresh save.
  const [savedToWorkspace, setSavedToWorkspace] = useState(false);
  const [savedToDept, setSavedToDept] = useState(false);
  // canSaveToDept comes from the parent and reflects role; auto-tick it on
  // first render so the existing curator UX (one click, save to dept) holds.
  useEffect(() => {
    setSaveToDept(canSaveToDept);
  }, [canSaveToDept]);
  // null = nothing expanded; '__all__' or a category slug when one is open.
  // Reset on close so the user always sees the collapsed tree on re-open.
  const [expandedGroup, setExpandedGroup] = useState(null);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  // Sync external value changes (e.g. when a library block fills the field).
  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  // Close dropdown on outside click — mousedown so it fires before the
  // listbox option's onClick (which is on click, after mousedown).
  useEffect(() => {
    function handleClick(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDropdown(false);
        setExpandedGroup(null);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Stale-response guard — see hooks/useSearchRequest.js. The 200ms debounce
  // below narrows the overlap window but never closes it, so without this the
  // hazard library list can be the answer to a prefix the assessor has already
  // finished typing over.
  const beginRequest = useLatestRequest();

  const fetchSuggestions = useCallback(async (q) => {
    const isCurrent = beginRequest();
    setLoading(true);
    try {
      const url = q
        ? `/api/risk-assessments/blocks?search=${encodeURIComponent(q)}`
        : '/api/risk-assessments/blocks';
      const res = await apiGet(url);
      // A non-ok response (network reachable, server said no) is exactly as
      // uninformative about "is this hazard already in the library" as a
      // rejected fetch — we never got a real answer, so it must not be read as
      // one. `if (res.ok)` with no `else` was half of this defect.
      if (!res.ok) throw new Error(`hazard blocks ${res.status}`);
      const data = await res.json();
      if (!isCurrent()) return;
      // Server returns the full alphabetised merged list (forks + seeds).
      // We render it grouped by category — the listbox is scrollable, so
      // showing everything lets the user see the real library size.
      const list = Array.isArray(data) ? data : [];
      setSuggestions(list);
      setFetchFailed(false);
    } catch {
      // The request never landed (blocked network, DNS, timeout) or the server
      // refused it — either way we do NOT know whether this hazard is already
      // in the library, so this must never be read as "no library match". See
      // fetchFailed, and the panel it drives below.
      if (!isCurrent()) return;
      setSuggestions([]);
      setFetchFailed(true);
    } finally {
      // Also guarded: a stale response clearing `loading` hides the spinner
      // while the real request is still open, and the empty-state condition
      // reads `!loading && suggestions.length === 0` — so an old empty result
      // would announce "No library match" for a hazard that does exist.
      if (isCurrent()) {
        setLoading(false);
        setHasFetchedOnce(true);
      }
    }
  }, [beginRequest]);

  const handleRetry = () => {
    fetchSuggestions(query);
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    setHasSelected(false);
    // Distinct query → distinct potential save. Forget what was saved for
    // the previous query so the next tick + save fires a fresh POST.
    setSavedToWorkspace(false);
    setSavedToDept(false);
    onChange?.(val);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 200);
    setShowDropdown(true);
  };

  const handleFocus = () => {
    if (disabled) return;
    setShowDropdown(true);
    if (!hasFetchedOnce) fetchSuggestions(query);
  };

  const handleSelect = (block) => {
    setQuery(block.name);
    setHasSelected(true);
    // Selecting a library hazard supersedes save-as for either target.
    setSaveToWorkspace(false);
    setSaveToDept(false);
    setSaveCategory('');
    setSavedToWorkspace(false);
    setSavedToDept(false);
    setSuggestions([]);
    setShowDropdown(false);
    setExpandedGroup(null);
    onSelect?.(block);
  };

  // Dispatch the save POST(s) for whichever targets are ticked AND haven't
  // already been saved for the current query. Marks the savedTo* flags so
  // re-ticking / blur / explicit re-click doesn't fire a second POST (server
  // would 409 on duplicate name). Crucially does NOT untick the checkbox —
  // the ticked state is the visual confirmation that the save was requested.
  const dispatchSave = () => {
    if (hasSelected || !query.trim()) return;
    const willSaveWs = saveToWorkspace && canSaveToWorkspace && !savedToWorkspace;
    const willSaveDept = saveToDept && canSaveToDept && !savedToDept;
    if (!willSaveWs && !willSaveDept) return;
    onSaveAsBlock?.(
      query.trim(),
      { toWorkspace: willSaveWs, toDept: willSaveDept },
      saveCategory || null,
    );
    if (willSaveWs) setSavedToWorkspace(true);
    if (willSaveDept) setSavedToDept(true);
  };

  const handleBlur = () => {
    // Always let the parent know — used to persist plain-text edits.
    onBlur?.(query);
    // Safety-net trigger: if user ticked + walked away without clicking the
    // explicit "Save block" button, fire it on blur.
    dispatchSave();
  };

  const canSaveAnywhere = canSaveToWorkspace || canSaveToDept;
  const showSaveCheckbox = canSaveAnywhere && !!query.trim() && !hasSelected;
  // Drives the conditional category + save button. We only show those once
  // the user has committed to saving by ticking at least one target.
  const anyTargetTicked = (saveToWorkspace && canSaveToWorkspace) || (saveToDept && canSaveToDept);
  // True if every ticked target has been dispatched for THIS query — used to
  // freeze the Save button + lock the category dropdown post-save.
  const alreadySaved =
    anyTargetTicked &&
    (!saveToWorkspace || !canSaveToWorkspace || savedToWorkspace) &&
    (!saveToDept || !canSaveToDept || savedToDept);

  // Bucket suggestions by category, plus a synthetic "All" group at the top
  // that holds every hazard alphabetised. The dropdown renders this as a
  // collapsible tree at empty state and as auto-expanded flat groups during
  // search.
  const groups = useMemo(() => {
    const buckets = new Map();
    for (const block of suggestions) {
      const key = block.category || '__uncategorised__';
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push(block);
    }
    const real = [...buckets.entries()]
      .map(([key, items]) => ({
        key,
        label: titleCaseCategory(key === '__uncategorised__' ? null : key),
        items,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
    const all = { key: '__all__', label: 'All', items: suggestions };
    return [all, ...real];
  }, [suggestions]);

  const isSearching = query.trim().length > 0;

  return (
    <div ref={wrapperRef} className="relative">
      <input
        type="text"
        role="combobox"
        value={query}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        className={DEFAULT_INPUT_CLASS}
        aria-haspopup="listbox"
        aria-expanded={showDropdown}
        aria-autocomplete="list"
        aria-controls={listboxId}
        data-testid="hazard-autocomplete-input"
      />
      <span
        className={`absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400 dark:text-gray-500 transition-transform ${showDropdown ? 'rotate-180' : ''}`}
        aria-hidden="true"
      >
        <ChevronDown className="w-5 h-5" />
      </span>

      {showDropdown && suggestions.length > 0 && (
        <div
          id={listboxId}
          role="listbox"
          className="absolute z-50 mt-1 w-full bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg max-h-72 overflow-y-auto py-1"
        >
          {(isSearching
            ? groups.filter((g) => g.key !== '__all__' && g.items.length > 0)
            : groups
          ).map((group) => {
            const isOpen = isSearching || expandedGroup === group.key;
            return (
              <div key={group.key} role="group" aria-label={group.label}>
                {isSearching ? (
                  <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 font-semibold">
                    {group.label} <span className="text-gray-400 dark:text-gray-500">· {group.items.length}</span>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() =>
                      setExpandedGroup((prev) => (prev === group.key ? null : group.key))
                    }
                    aria-expanded={isOpen}
                    className="w-full flex items-center justify-between px-3 py-1.5 text-sm font-medium text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600/40"
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      <ChevronDown
                        className={`w-3.5 h-3.5 text-gray-400 dark:text-gray-500 shrink-0 transition-transform ${isOpen ? '' : '-rotate-90'}`}
                      />
                      <span className="truncate">{group.label}</span>
                    </span>
                    <span className="text-[11px] text-gray-400 dark:text-gray-500 shrink-0 ml-2">
                      {group.items.length}
                    </span>
                  </button>
                )}
                {isOpen &&
                  group.items.map((block) => (
                    <button
                      key={`${block.kind}-${block.id}`}
                      type="button"
                      role="option"
                      aria-selected={false}
                      onClick={() => handleSelect(block)}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 dark:hover:bg-gray-600 ${
                        isSearching ? '' : 'pl-9'
                      }`}
                    >
                      <span className="flex items-center gap-1.5 min-w-0">
                        <span className="block text-gray-800 dark:text-gray-200 truncate flex-1 min-w-0">
                          {block.name}
                        </span>
                        {!!block.is_personal && (
                          <span className="shrink-0 text-[9px] uppercase tracking-wider px-1 py-0.5 rounded bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                            Personal
                          </span>
                        )}
                      </span>
                      {block.description && (
                        <span className="block text-[11px] text-gray-500 dark:text-gray-400 truncate">
                          {block.description}
                        </span>
                      )}
                    </button>
                  ))}
              </div>
            );
          })}
        </div>
      )}
      {showDropdown && loading && (
        <div className="absolute z-50 mt-1 w-full bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg px-3 py-2 text-xs text-gray-400 dark:text-gray-500">
          Searching…
        </div>
      )}
      {/* "No library match" is a CLAIM ABOUT THE LIBRARY, and it must only be
          made when the library actually answered. `!fetchFailed` is the term
          that makes it one: without it a blocked or refused request also reads
          `suggestions.length === 0`, and this panel told a safety author that a
          regulated control set was absent — with "Save to department library"
          already ticked beside it — for a hazard nobody ever asked about. */}
      {showDropdown && !loading && hasFetchedOnce && !fetchFailed && suggestions.length === 0 && query.trim().length >= 2 && (
        <div className="absolute z-50 mt-1 w-full bg-white dark:bg-gray-700 border border-amber-300 dark:border-amber-700 rounded-lg shadow-lg px-3 py-2 text-xs">
          <p className="text-amber-700 dark:text-amber-300 font-medium">
            No library match for &ldquo;{query}&rdquo;
          </p>
          <p className="text-gray-500 dark:text-gray-400 mt-0.5">
            You can keep typing a custom hazard
            {canSaveAnywhere ? ' and tick a box below to save it for next time.' : '.'}
          </p>
        </div>
      )}
      {/* The request never landed or the server refused it. Say that, never
          "no library match", and never invite a save — a duplicate block in a
          regulated safety library is the harm this panel exists to avoid. The
          server 409s on a duplicate name, so the best case is a save that
          silently fails and the worst is a second copy of a control set. */}
      {showDropdown && !loading && hasFetchedOnce && fetchFailed && (
        <div className="absolute z-50 mt-1 w-full bg-white dark:bg-gray-700 border border-red-300 dark:border-red-700 rounded-lg shadow-lg px-3 py-2 text-xs">
          <p className="text-red-700 dark:text-red-300 font-medium">Couldn&rsquo;t reach the hazard library</p>
          <p className="text-gray-500 dark:text-gray-400 mt-0.5">
            We don&rsquo;t know whether this hazard is already in the library, so
            saving it now could create a duplicate. Check your connection and try
            again.
          </p>
          {/* 🪤 `onMouseDown` preventDefault, and it is load-bearing HERE in a
              way it is not in UserAutocomplete's identical panel: this input's
              `onBlur` runs `dispatchSave()`, and "Save to department library"
              is auto-ticked for curators. Without this, pressing Retry blurs
              the input first and fires the very duplicate save the panel is
              warning against. Keeping focus on the input also means the
              refreshed dropdown is still open when the answer arrives. */}
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleRetry}
            className="mt-1 text-indigo-600 dark:text-indigo-400 font-medium hover:underline"
            data-testid="hazard-autocomplete-retry"
          >
            Retry
          </button>
        </div>
      )}

      {showSaveCheckbox && (
        <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-600 dark:text-gray-400">
          {canSaveToDept && (
            <label className="inline-flex items-center gap-1.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={saveToDept}
                onChange={(e) => setSaveToDept(e.target.checked)}
                disabled={disabled}
                className="rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500"
                data-testid="hazard-autocomplete-save-as-block-dept"
              />
              <span>Save to department library</span>
            </label>
          )}
          {canSaveToWorkspace && (
            <label className="inline-flex items-center gap-1.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={saveToWorkspace}
                onChange={(e) => setSaveToWorkspace(e.target.checked)}
                disabled={disabled}
                className="rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500"
                data-testid="hazard-autocomplete-save-as-block-workspace"
              />
              <span>Save as new hazard template to my workspace</span>
            </label>
          )}
          {/* Category + explicit Save button appear only after the user has
              committed to saving (ticked at least one target). Avoids extra
              chrome for the typical "just type a hazard" path. */}
          {anyTargetTicked && (
            <>
              <label className="inline-flex items-center gap-1.5">
                <span className="text-gray-500 dark:text-gray-400">Category</span>
                <select
                  value={saveCategory}
                  onChange={(e) => setSaveCategory(e.target.value)}
                  disabled={disabled || alreadySaved}
                  className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 text-xs px-1.5 py-0.5 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-60"
                  data-testid="hazard-autocomplete-save-as-block-category"
                >
                  <option value="">Uncategorised</option>
                  {CATEGORY_OPTIONS.map((c) => (
                    <option key={c.slug} value={c.slug}>{c.label}</option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                onClick={dispatchSave}
                disabled={disabled || alreadySaved}
                // "Save block" SAVES, so it is the small green house Save
                // (owner ruling 2026-09-25; it was a non-house indigo). The
                // "✓ Saved" state is a STATE, not an action, and keeps its
                // pale green unchanged.
                className={`${
                  alreadySaved
                    ? 'px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300 cursor-default'
                    : BTN_XS.confirm
                } disabled:opacity-60`}
                data-testid="hazard-autocomplete-save-as-block-button"
              >
                {alreadySaved ? '✓ Saved' : 'Save block'}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
